/**
 * Public activity catalogue API (CONTRACTS.md §4).
 *
 * Consumers: Planner deck, Vandaag, Gezondheid. Import from
 * `@/data/activities` only.
 */
import { ACTIVITIES, ACTIVITY_CATEGORIES } from './activities'
import type { Activity } from './types'

export type { Activity, ActivityCategory } from './types'
export { ACTIVITIES, ACTIVITY_CATEGORIES }

const byId = new Map<string, Activity>(ACTIVITIES.map((activity) => [activity.id, activity]))

/**
 * Legacy / duplicate activity ids mapped to their canonical replacement so
 * saved planner items survive catalogue clean-ups. Applied by consumers (and
 * the migration layer) before lookup — never silently drop user items.
 *
 * - `wakker-worden*` → `opstaan` (duplicate of Opstaan removed)
 * - `pomodoro` → `focusblok` (Pomodoro removed from the deck)
 * - `dwijlen` → `dweilen` (spelling defect in legacy builds)
 */
export const ACTIVITY_ID_ALIASES: Record<string, string> = {
  'wakker-worden': 'opstaan',
  'wakker-worden-slaap': 'opstaan',
  'wakker-worden-en-slaap': 'opstaan',
  pomodoro: 'focusblok',
  'pomodoro-sessie': 'focusblok',
  dwijlen: 'dweilen',
  bad: 'bad-nemen',
  whatsapp: 'whatsapp-bekijken',
  'whatsapp-checken': 'whatsapp-bekijken',
  gamen: 'game-spelen',
  games: 'game-spelen',
  'eten-voorbereiding': 'eten-voorbereiden',
  'e-mailen': 'mailen',
  'rust-moment': 'rustmoment',
  'dutje-doen': 'dutje',
}

/** Resolves a possibly-legacy activity id to the canonical id. */
export function resolveActivityId(id: string): string {
  const direct = byId.has(id)
  if (direct) return id
  const alias = ACTIVITY_ID_ALIASES[id]
  if (alias && byId.has(alias)) return alias
  return id
}

/** Looks up an activity by id, following ACTIVITY_ID_ALIASES for legacy ids. */
export function getActivityById(id: string): Activity | undefined {
  return byId.get(resolveActivityId(id))
}

/**
 * Accent-insensitive case-folded search over the canonical deck order.
 * An empty/whitespace query returns every activity (optionally filtered by
 * category); a categoryId narrows the result to that category.
 */
export function searchActivities(query: string, categoryId?: string): Activity[] {
  const normalizedQuery = normalize(query)
  return ACTIVITIES.filter((activity) => {
    if (categoryId && activity.categoryId !== categoryId) return false
    if (!normalizedQuery) return true
    return normalize(activity.name).includes(normalizedQuery)
  })
}

function normalize(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
}
